import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { format, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Wrench, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

// Timeline rail (Option C) — vertical rail of working days, dots coloured by
// state, IN / OUT chips per day. Missed punch-outs are highlighted in red and
// keep the existing "Request correction" affordance.

const monthLabel = (ym) => {
  // ym === "2026-08"
  try {
    const [y, m] = ym.split("-").map(Number);
    return format(new Date(y, m - 1, 1), "MMMM yyyy");
  } catch {
    return ym;
  }
};

const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function EmployeeHistory() {
  const [rows, setRows] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [corrections, setCorrections] = useState([]);
  const [loading, setLoading] = useState(false);

  // Correction dialog
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ time: "18:00", reason: "" });
  const [submitting, setSubmitting] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        api.get("/attendance/mine", { params: { month } }),
        api.get("/corrections/mine"),
      ]);
      setRows(a.data || []);
      setCorrections(c.data || []);
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, [month]);

  const corrFor = (attId) => corrections.find((c) => c.attendance_id === attId);

  // Ascending chronological order — matches the mockup and reads like a
  // month timeline. Backend returns DESC so we reverse on the client.
  const timeline = useMemo(() => {
    return [...rows].sort((a, b) => a.date.localeCompare(b.date));
  }, [rows]);

  const summary = useMemo(() => {
    let present = 0;
    let missed = 0;
    let hours = 0;
    for (const r of timeline) {
      if (r.punch_in_time) present += 1;
      if (r.punch_in_time && !r.punch_out_time) missed += 1;
      hours += Number(r.working_hours || 0);
    }
    return { present, missed, hours: hours.toFixed(1) };
  }, [timeline]);

  const startRequest = (r) => {
    setTarget(r);
    setForm({ time: "18:00", reason: "" });
    setOpen(true);
  };
  const submitRequest = async () => {
    setSubmitting(true);
    try {
      await api.post("/corrections", {
        attendance_id: target.id,
        proposed_punch_out_time: form.time,
        reason: form.reason,
      });
      toast.success("Correction request submitted");
      setOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="py-2 pb-8">
      {/* Header */}
      <div className="mb-4">
        <div className="label-mono">History</div>
        <h1 className="text-2xl font-display font-black tracking-tight mt-0.5">My month</h1>
        <p className="text-xs text-muted-foreground mt-1">Scroll through each working day</p>
      </div>

      {/* Month nav */}
      <div className="flex items-center justify-between border border-border px-3 py-2 mb-4">
        <button
          data-testid="history-prev-month"
          onClick={() => setMonth(shiftMonth(month, -1))}
          className="p-1 text-muted-foreground hover:text-foreground"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-5 w-5" strokeWidth={1.5} />
        </button>
        <div className="relative">
          <div className="font-display font-bold text-sm">{monthLabel(month)}</div>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="history-month"
            className="absolute inset-0 opacity-0 cursor-pointer"
            aria-label="Pick month"
          />
        </div>
        <button
          data-testid="history-next-month"
          onClick={() => setMonth(shiftMonth(month, 1))}
          className="p-1 text-muted-foreground hover:text-foreground"
          aria-label="Next month"
        >
          <ChevronRight className="h-5 w-5" strokeWidth={1.5} />
        </button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-2 mb-5" data-testid="history-summary">
        <div className="border border-border p-2">
          <div className="label-mono">Present</div>
          <div className="font-mono-brand text-lg font-bold" data-testid="summary-present">{summary.present}</div>
        </div>
        <div className="border border-border p-2">
          <div className="label-mono">Hours</div>
          <div className="font-mono-brand text-lg font-bold" data-testid="summary-hours">{summary.hours}</div>
        </div>
        <div className={`border p-2 ${summary.missed > 0 ? "border-red-500/40 bg-red-500/5" : "border-border"}`}>
          <div className="label-mono">Missed</div>
          <div
            data-testid="summary-missed"
            className={`font-mono-brand text-lg font-bold ${summary.missed > 0 ? "text-red-400" : ""}`}
          >
            {summary.missed}
          </div>
        </div>
      </div>

      {/* Timeline rail */}
      {loading && timeline.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-10 font-mono-brand">Loading…</div>
      ) : timeline.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-10">No records for this month.</div>
      ) : (
        <div className="relative pl-8" data-testid="timeline-rail">
          {/* The rail */}
          <div className="absolute left-3 top-2 bottom-2 w-px bg-border" />
          {timeline.map((r) => {
            const missed = !!r.punch_in_time && !r.punch_out_time;
            const corr = corrFor(r.id);
            const showRequestBtn = missed && !corr;
            const dateObj = parseISO(r.date);
            return (
              <div
                key={r.id}
                data-testid={`history-${r.date}`}
                className="relative py-3 border-b border-dashed border-border last:border-b-0"
              >
                {/* Dot */}
                <span
                  className={`absolute -left-[21px] top-[18px] w-3 h-3 rounded-full ring-2 ring-background ${
                    missed ? "bg-red-500" : "bg-emerald-500"
                  }`}
                />
                <div className={`font-display font-bold text-sm ${missed ? "text-red-300" : ""}`}>
                  {format(dateObj, "EEE d MMM")}
                </div>
                {r.center_name && (
                  <div className="text-[10px] text-muted-foreground uppercase tracking-widest font-mono-brand mt-0.5">
                    {r.center_name}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  {r.punch_in_time ? (
                    <span
                      className="font-mono-brand text-[11px] px-2 py-0.5 border border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                      data-testid={`in-${r.date}`}
                    >
                      IN {format(parseISO(r.punch_in_time), "HH:mm")}
                    </span>
                  ) : (
                    <span className="font-mono-brand text-[11px] px-2 py-0.5 border border-border text-muted-foreground">
                      NO PUNCH-IN
                    </span>
                  )}
                  {r.punch_out_time ? (
                    <span
                      className="font-mono-brand text-[11px] px-2 py-0.5 border border-primary/40 bg-primary/10 text-primary"
                      data-testid={`out-${r.date}`}
                    >
                      OUT {format(parseISO(r.punch_out_time), "HH:mm")}
                    </span>
                  ) : r.punch_in_time ? (
                    <span
                      className="font-mono-brand text-[11px] px-2 py-0.5 border border-red-500/50 bg-red-500/15 text-red-300 font-bold"
                      data-testid={`missed-${r.date}`}
                    >
                      MISSED PUNCH-OUT
                    </span>
                  ) : null}
                  {r.punch_in_time && r.punch_out_time && (
                    <span className="font-mono-brand text-[11px] text-muted-foreground ml-1">
                      · {Number(r.working_hours || 0).toFixed(1)}h
                    </span>
                  )}
                </div>

                {corr && (
                  <div
                    data-testid={`corr-status-${corr.id}`}
                    className="mt-2 text-[10px] font-mono-brand uppercase tracking-widest inline-flex items-center gap-2"
                  >
                    <span className="text-muted-foreground">Correction · proposed {format(parseISO(corr.proposed_punch_out_time), "HH:mm")}</span>
                    <span
                      className={`px-1.5 py-0.5 border ${
                        corr.status === "approved"
                          ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                          : corr.status === "rejected"
                          ? "bg-red-500/10 text-red-300 border-red-500/30"
                          : "bg-yellow-400/15 text-yellow-200 border-yellow-400/40"
                      }`}
                    >
                      {corr.status}
                    </span>
                  </div>
                )}

                {showRequestBtn && (
                  <Button
                    data-testid={`request-correction-${r.id}`}
                    onClick={() => startRequest(r)}
                    variant="outline"
                    size="sm"
                    className="mt-2 h-7 px-2 rounded-none border-[hsl(43_96%_56%)]/40 text-[hsl(43_96%_60%)] hover:bg-[hsl(43_96%_56%)]/10 text-[11px] font-mono-brand"
                  >
                    <Wrench className="h-3 w-3 mr-1.5" strokeWidth={1.5} /> Request correction
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Correction dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none bg-card border-border">
          <DialogHeader><DialogTitle className="font-display">Request punch-out correction</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {target && (
              <div className="border border-border p-3">
                <div className="label-mono">Day</div>
                <div className="text-sm font-medium">{format(parseISO(target.date), "EEE, d MMM yyyy")}</div>
                <div className="label-mono mt-2">Punched in</div>
                <div className="font-mono-brand text-sm">{target.punch_in_time ? format(parseISO(target.punch_in_time), "HH:mm 'UTC'") : "—"}</div>
              </div>
            )}
            <div>
              <Label className="label-mono">Actual punch-out time (UTC · HH:MM)</Label>
              <Input
                data-testid="correction-time-input"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                placeholder="18:30"
                className="rounded-none font-mono-brand"
              />
            </div>
            <div>
              <Label className="label-mono">Reason</Label>
              <Input
                data-testid="correction-reason-input"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="Forgot to punch out; left the office at 18:30"
                className="rounded-none"
              />
              <div className="label-mono mt-1">Your Center Admin will review this request.</div>
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="submit-correction-btn"
              onClick={submitRequest}
              disabled={submitting}
              className="rounded-none btn-primary-brand"
            >
              {submitting ? "Submitting…" : "Submit request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
